/**
 * MessageItem Component
 *
 * Displays a single message in a conversation with appropriate styling
 * for user vs assistant messages, handles RTL/LTR text, attachments,
 * status badges, and action buttons.
 */

import React from 'react';
import type { Message, PrototyperBuildTimeline } from '@ideoz/core';
import {
  messageHasOwnBuild,
  messageHasPrototyperArtifact,
  resolvePrototypeTitle,
  useAttachmentDisplay,
} from '@ideoz/core';
import PrototyperReasoningTimeline from './PrototyperReasoningTimeline';
import { ThinkingPanel } from './ThinkingPanel';
import { ArtifactPill } from './ArtifactPill';
import { PrototypePill } from './PrototypePill';
import type { MessageArtifact } from '@ideoz/core';
import UserMessage from './user-message';
import IdeozMessage from './ideoz-message';
import { Button } from '../atoms/button';
import { Badge } from '../atoms/badge';
import FilePreview from './file-preview';
import Icon from './Icon';
import {
  Check,
  Info,
  TriangleAlert,
  ThumbsUp,
  ThumbsDown,
  Copy,
  RotateCcw,
  MoreHorizontal,
  ArrowUpWideNarrow,
} from 'lucide-react';
// Analytics: events tracked via data-track attributes and Redux middleware
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../atoms/tooltip';
import { copyMarkdownAsHTML } from '../../utils/messageFormatting';
import { div } from 'motion/react-client';

// ============================================================================
// TYPES
// ============================================================================

export interface MessageItemProps {
  conversationId?: string | null;
  projectId?: string | null;
  message: Message;
  isLastMessage: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isClarification: boolean;
  /** MCQ options from SSE clarification event (last assistant message only) */
  clarificationOptions?: string[];
  /** Live agent progress for the streaming assistant turn */
  streamProgress?: { phase?: string; nodeId?: string; toolName?: string } | null;
  /** Ordered "thinking" steps rendered inside the ThinkingPanel above the reply. */
  streamThoughts?: import('@ideoz/core').ThoughtStep[];
  /** True once tokens have started streaming this turn — collapses the panel. */
  hasStartedResponse?: boolean;
  /** Wall-clock ms elapsed while thinking (null while ongoing). */
  thinkingDurationMs?: number | null;
  shouldShowBanner: boolean;
  isReadyToCreate: boolean;
  isStreaming: boolean;
  isLoading: boolean;
  /**
   * Fired when the user clicks an inline streamed-artifact pill. Host opens
   * the side dialog with the persisted content.
   */
  onStreamedArtifactOpen?: (artifact: MessageArtifact) => void;
  onPrototyperClick?: (
    prototyperDemoLinkId?: string | number,
    // `continueBuild`: the pill's Continue button, on a build that stopped before it passed.
    // `didNotFinish`: the pill showed "Didn't finish" when it was clicked.
    meta?: { llmInteractionId?: string | null; continueBuild?: boolean; didNotFinish?: boolean }
  ) => void;

  // Actions
  onSubmit: (text: string) => void;
  onFeedback?: (
    type: 'like' | 'dislike',
    messageID?: string | null,
    projectId?: string | null
  ) => void | Promise<void>;
  setIsReflection: (value: boolean) => void;
  setIsRepeated: (value: boolean) => void;
  onRetryDrawing?: (drawType: string) => void;
  forcedDirection?: 'rtl' | 'ltr';
  isPrototypeOpen?: boolean;
  isPrototyper?: boolean;
  /** Nested under the assistant bubble (e.g. plugin / legacy workbench slot). */
  streamingWorkbenchSlot?: React.ReactNode;
  /** WebContainer persisted or live build timeline */
  prototyperBuildTimeline?: PrototyperBuildTimeline | null;
  isWorkbenchBuildLive?: boolean;
  /** When true, show Finalizing + Done timeline rows (persisted success or live idle + prod build ok). */
  showPrototyperTimelineTerminalPhases?: boolean;
  activeSnapshotInteractionId?: string | null;
  restoreInteractionLoadingId?: string | null;
  /** Whether this turn's build stopped before it passed; its pill then offers Continue. */
  isPrototypeBuildUnfinished?: (llmInteractionId: string) => boolean;
  /**
   * Whether the turn's unfinished build was removed by the founder or replaced by a newer
   * finished one. Its card then says so and has no Continue.
   */
  prototypeBuildSetAside?: (llmInteractionId: string) => 'removed' | 'replaced' | undefined;
  /**
   * What the button on an unfinished build does: `continue` finishes it from where it stopped,
   * `build-again` runs its request once more from the last finished version (a failed build),
   * `none` shows no button (an older unfinished build of a prototype: only its newest one can be
   * finished or built again). The host decides what the click does; this only names the button.
   */
  prototypeBuildAction?: (
    llmInteractionId: string
  ) => 'continue' | 'build-again' | 'none' | undefined;
  /**
   * Whether this turn has a finished (verified) version. False leaves the version number off its
   * pill: a turn about to build read "V1" before it had any version at all.
   */
  isPrototypeVersionSettled?: (llmInteractionId: string) => boolean;
  /**
   * The turn's version number (per prototype, from the backend), when the caller knows it. It
   * wins over the message's `prototypeVersionIndex`, which a live turn only gets once verified.
   */
  prototypeVersionNumber?: (llmInteractionId: string) => number | undefined;
  /** Pill text while `restoreInteractionLoadingId` is this turn; "Restoring…" when absent. */
  restoreLoadingLabel?: string;
  /** Label while this turn's build is live (e.g. an automatic fix attempt); default "Building…". */
  liveBuildLabel?: string;
  /** Rendered under the prototype card: the "needs your input" card (P6.4b). */
  prototypeFixSlot?: React.ReactNode;
  /**
   * False for a scope's auto-prompt greeting (streams in as the opening message
   * with no user turn before it — see useConversationPageFacade). The backend can
   * still tag that greeting `mode: 'prototyper'` when continuing a scope that's
   * mid-build, but it's a conversational reply, not an actual prototype — so the
   * "View prototype" pill must not render under it. Defaults to true so callers
   * that don't track conversation position (e.g. a single-message preview) keep
   * today's behavior.
   */
  hasPrecedingUserMessage?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * First strongly-directional character wins (the Unicode BiDi "first strong"
 * rule): the first Arabic/Hebrew letter → RTL, the first Latin/Cyrillic/Greek
 * letter → LTR. Neutral characters — digits, punctuation, markdown symbols
 * (`#`, `-`, `*`), emoji and whitespace — are skipped, so "### مرحبا" or
 * "1. Hello" is classified by its first real word, not a leading symbol.
 * Defaults to LTR when the text has no strong character (empty / punctuation).
 */
const RTL_LETTER =
  /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const LTR_LETTER = /[A-Za-z\u00C0-\u02AF\u0370-\u03FF\u0400-\u04FF]/;

const detectDirectionFromContent = (text: string): 'rtl' | 'ltr' => {
  for (const char of text) {
    if (RTL_LETTER.test(char)) return 'rtl';
    if (LTR_LETTER.test(char)) return 'ltr';
  }
  return 'ltr';
};

/**
 * Clean user message to remove attachment markers
 */
const cleanUserMessage = (content: string): string => {
  if (!content) return '';
  // Remove attachment information like "Attachments: file1.pdf, file2.txt"
  return content.replace(/Attachments?:\s*[^\n]+/gi, '').trim();
};

// ============================================================================
// COMPONENT
// ============================================================================

export const MessageItem: React.FC<MessageItemProps> = ({
  conversationId,
  projectId,
  message,
  isLastMessage,
  isReflection,
  isRepeated,
  isClarification,
  clarificationOptions = [],
  streamProgress = null,
  streamThoughts,
  hasStartedResponse,
  thinkingDurationMs,
  shouldShowBanner,
  isReadyToCreate,
  isStreaming,
  isLoading,
  onPrototyperClick,
  onStreamedArtifactOpen,
  onSubmit,
  onFeedback,
  setIsReflection,
  setIsRepeated,
  onRetryDrawing,
  forcedDirection,
  isPrototypeOpen,
  isPrototyper,
  streamingWorkbenchSlot,
  prototyperBuildTimeline = null,
  isWorkbenchBuildLive = false,
  showPrototyperTimelineTerminalPhases = false,
  activeSnapshotInteractionId = null,
  restoreInteractionLoadingId = null,
  isPrototypeBuildUnfinished,
  prototypeBuildSetAside,
  prototypeBuildAction,
  isPrototypeVersionSettled,
  prototypeVersionNumber,
  restoreLoadingLabel,
  hasPrecedingUserMessage = true,
  liveBuildLabel,
  prototypeFixSlot,
}) => {
  // ========== State ==========
  const [feedback, setFeedback] = React.useState<'none' | 'like' | 'dislike'>(
    // Initialize from message.isLiked if available
    (message as any).isLiked === true
      ? 'like'
      : (message as any).isLiked === false
        ? 'dislike'
        : 'none'
  );
  const [isCopied, setIsCopied] = React.useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = React.useState(false);

  // ========== Effects ==========

  // Update feedback state when message.isLiked changes
  React.useEffect(() => {
    const messageIsLiked = (message as any).isLiked;
    const newFeedback =
      messageIsLiked === true ? 'like' : messageIsLiked === false ? 'dislike' : 'none';

    if (newFeedback !== feedback) {
      setFeedback(newFeedback);
    }
  }, [(message as any).isLiked]);

  // ========== Fetch Attachments ==========

  const { attachments: coreAttachments, isLoading: isLoadingAttachments } = useAttachmentDisplay({
    attachmentIds: (message as any).attachments_ids || [],
  });

  // Convert core attachments to UI format
  const attachments = coreAttachments.map((att) => ({
    id: att.id,
    file: new File([], att.name, { type: att.type }),
    preview: att.preview,
    isImage: att.isImage,
    isUploading: false,
    uploadProgress: 100,
  }));

  // ========== Derived State ==========

  // Direction is read from the message's own content (first strong character),
  // not from the persisted `detectedLanguage` hint — the backend sometimes
  // stores the wrong language (e.g. `en` on an Arabic reply), which would
  // otherwise flip a clearly-Arabic message to LTR on reload.
  const computedIsRtl = detectDirectionFromContent(message.content || '') === 'rtl';
  // The turn's build stopped before it passed: the pill says so and offers Continue, and the
  // timeline stops saying "Built in Xs" (test finding 2).
  const prototypeBuildDidNotFinish = Boolean(
    !isWorkbenchBuildLive &&
    message.llmInteractionId &&
    isPrototypeBuildUnfinished?.(message.llmInteractionId)
  );
  const prototypeSetAside =
    !isWorkbenchBuildLive && message.llmInteractionId
      ? prototypeBuildSetAside?.(message.llmInteractionId)
      : undefined;
  // The unfinished build's button: Continue, Build again, or none on an older one.
  const unfinishedButton = message.llmInteractionId
    ? prototypeBuildAction?.(message.llmInteractionId)
    : undefined;
  const isRtl = forcedDirection ? forcedDirection === 'rtl' : computedIsRtl;

  const hasAttachments = (message as any).attachments_ids?.length > 0;

  // ========== Handlers ==========

  const handleActionClick = (text: string) => {
    onSubmit(text);
    setIsReflection(false);
    setIsRepeated(false);
  };

  const handleRetryDrawing = () => {
    if (onRetryDrawing && (message as any).drawType) {
      // Analytics: drawing.creation.retried tracked via data-track attribute
      onRetryDrawing((message as any).drawType);
    }
  };

  const handleFeedback = async (type: 'like' | 'dislike') => {
    if (!onFeedback) return;

    // Store previous state for potential rollback
    const previousFeedback = feedback;
    const newFeedback = previousFeedback === type ? 'none' : type;

    // Optimistic UI update
    setFeedback(newFeedback);

    setIsSendingFeedback(true);
    try {
      // Use llmInteractionId (real backend UUID) if available, otherwise fall back to id (timestamp)
      const interactionId = (message as any).llmInteractionId || message.id;
      await onFeedback(type, interactionId, projectId);
    } catch (err) {
      console.error('Failed to send feedback', err);
      // Revert optimistic update on error
      setFeedback(previousFeedback);
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const handleCopyMessage = async () => {
    try {
      const content = message.content || '';

      // Copy as HTML (supports all markdown elements: headings, lists, tables, code blocks, etc.)
      // Falls back to plain markdown if HTML paste is not supported
      const success = await copyMarkdownAsHTML(content);

      if (success) {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } else {
        // Final fallback: copy as plain text
        await navigator.clipboard.writeText(content);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy message', err);
    }
  };

  // ========== Render Helpers ==========

  /**
   * Render drawing status banner (success/error)
   */
  const renderBanner = (className?: string) => {
    const bannerType = (message as any).bannerType;
    const drawType = (message as any).drawType;
    const bannerText = (message as any).bannerText;

    if (!bannerType) return null;

    return (
      <div className={`flex items-center w-full ${isRtl ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Kept within the chat column: a long message with "Try again" used to widen the badge
            past it and give the chat a horizontal scrollbar. */}
        <Badge
          variant={bannerType}
          dir={isRtl ? 'rtl' : 'ltr'}
          className="max-w-full min-w-0"
          childrenClasses="flex min-w-0 flex-1 items-center whitespace-normal"
          leftIcon={
            <Icon
              icon={bannerType === 'success' ? Check : TriangleAlert}
              size={18}
              strokeWidth={2}
              color={bannerType === 'success' ? '#067647' : '#D92D20'}
            />
          }
        >
          <div className={`flex min-w-0 flex-1 items-center gap-2 ${isRtl ? '' : 'justify-between'}`}>
            {bannerText ? (
              bannerText
            ) : bannerType === 'success' ? (
              isRtl ? (
                <>
                  لقد أكمل <span style={{ direction: 'ltr', display: 'inline' }}>Ideoz AI</span>{' '}
                  إنشاء{' '}
                  <span style={{ direction: 'ltr', display: 'inline' }}>
                    {drawType?.replace(/_/g, ' ')}
                  </span>{' '}
                  بنجاح
                </>
              ) : drawType === 'prototyper' ? (
                'Ideoz AI has completed creating the prototyper successfully'
              ) : (
                `Ideoz AI has completed the generating ${drawType?.replace(/_/g, ' ')} successfully`
              )
            ) : isRtl ? (
              drawType === 'prototyper' ? (
                'حدث خطأ ما، يرجى المحاولة مرة أخرى'
              ) : (
                'عذراً، شيء لم يسر كما هو مخطط له.'
              )
            ) : drawType === 'prototyper' ? (
              'Something went wrong, please try again'
            ) : (
              "Oops, something didn't go as planned."
            )}

            {bannerType === 'error' && drawType && onRetryDrawing && drawType === 'prototyper' && (
              <Button
                onClick={handleRetryDrawing}
                variant="link-gray"
                size="sm"
                className="py-0 h-2 shrink-0"
                data-track="try_again"
              >
                {isRtl ? 'حاول مرة أخرى' : 'Try again'}
              </Button>
            )}
          </div>
        </Badge>
      </div>
    );
  };

  /**
   * Render message content based on role
   */
  const renderMessageContent = () => {
    // User message
    if (message.role === 'user') {
      // Interactive-question answer: render the Q&A as a summary card instead
      // of a plain chat bubble (the founder clicked options; their selection is
      // shown here, while the actual answer went to the backend as ids).
      const questionAnswers = (message as any).questionAnswers as
        | Array<{ question: string; answer: string }>
        | undefined;
      if (questionAnswers && questionAnswers.length > 0) {
        return (
          <div
            dir="ltr"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-900"
            data-testid="message-question-answers"
          >
            {questionAnswers.map((qa, i) => (
              <div key={i} className={i > 0 ? 'mt-3' : ''}>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {qa.question}
                </div>
                <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{qa.answer}</div>
              </div>
            ))}
          </div>
        );
      }

      const cleanedContent = cleanUserMessage(message.content || '');
      const hasText = cleanedContent && cleanedContent.trim();
      // What the founder was pointing at when they wrote this - the element they picked in the
      // prototype editor. It sat on the composer while they typed; it belongs with the message
      // afterwards, or the conversation loses what the words were about.
      const element = (message as any).element as
        | { label: string; screenshot?: string }
        | undefined;

      if (!hasText && !hasAttachments && !element) return null;

      return (
        <>
          {element && (
            <div className="mb-1.5 flex justify-end">
              <div
                className="flex max-w-[80%] items-center gap-2 rounded-xl border border-gray-200 bg-white py-1 pl-1 pr-2.5 dark:border-gray-700 dark:bg-gray-900"
                data-testid="message-element"
              >
                {element.screenshot ? (
                  <img
                    src={element.screenshot}
                    alt=""
                    className="h-8 w-[52px] shrink-0 rounded-lg bg-white object-contain p-px"
                  />
                ) : null}
                <span className="truncate text-xs text-gray-600 dark:text-gray-300">
                  {element.label}
                </span>
              </div>
            </div>
          )}
          {hasText && (
            <div className="flex justify-end max-w-[80%] min-w-[100%]">
              <UserMessage dir={isRtl ? 'rtl' : 'ltr'} isPrototypeOpen={isPrototypeOpen}>
                {cleanedContent}
              </UserMessage>
            </div>
          )}

          {hasAttachments && attachments.length > 0 && (
            <div className="mt-2 ml-7">
              <FilePreview files={attachments as any} hasDelete={false} />
            </div>
          )}
        </>
      );
    }

    // Assistant message. The thinking panel now lives under the agent header
    // (passed into IdeozMessage as `thinkingSlot`), so we still render the
    // message shell while the agent is only thinking (content not arrived yet)
    // — that's how "Nexus" + the "Thinking…" panel appear together up front.
    const thinkingNode = renderStreamProgress();
    if (!message.content && !thinkingNode) return null;

    // Special handling for reflection/repeated messages
    if ((isReflection || isRepeated) && isLastMessage) {
      return (
        <IdeozMessage
          isReflection={isReflection}
          isRepeated={isRepeated}
          onActionClick={handleActionClick}
          isRtl={isRtl}
        >
          {message.content}
        </IdeozMessage>
      );
    }

    {
      const messageID = (message as any).id;

      return (
        <div className="flex flex-col max-w-full">
          {(message as any).hasBanner &&
            (message as any).bannerPosition === 'top' &&
            renderBanner('mb-6')}
          <IdeozMessage isRtl={isRtl} thinkingSlot={thinkingNode}>
            {message.content ?? ''}
          </IdeozMessage>
        </div>
      );
    }
  };

  /**
   * Success / status banner (bottom) — rendered after plan markdown and timeline.
   */
  const renderAssistantBottomBanner = () => {
    if (message.role !== 'assistant') return null;
    if (!(message as any).hasBanner || (message as any).bannerPosition !== 'bottom') return null;
    return <div className={`w-full max-w-full `}>{renderBanner('mt-0 mb-4')}</div>;
  };

  /**
   * Render feedback controls (like, dislike, copy)
   */
  const renderFeedbackControls = () => {
    // Only show for assistant messages that are not reflection/repeated/clarification
    if (message.role !== 'assistant') return null;
    if (isReflection || isRepeated) return null;
    if (!message.content) return null;
    if ((isLoading || isStreaming) && isLastMessage) return null;
    // Don't show feedback controls if there's an error banner
    if ((message as any).hasBanner && (message as any).bannerType === 'error') return null;

    return (
      // Always LTR + left-aligned, regardless of the message direction — the
      // like/dislike/copy row shouldn't flip to the right on Arabic replies
      // (`self-start` overrides the column's `items-end` in RTL).
      <div dir="ltr" className={`flex items-center gap-1 ms-8 mt-1 mb-2 self-start`}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild className="!p-0 w-auto">
              <Button
                variant="ghost"
                size="xs"
                className="h-auto !p-1 hover:bg-gray-200"
                onClick={() => handleFeedback('like')}
                aria-pressed={feedback === 'like'}
                aria-label="Like answer"
                data-testid="btn-like"
                data-track="message_like"
                disabled={isSendingFeedback}
              >
                <Icon
                  icon={ThumbsUp}
                  size={12}
                  color={
                    feedback === 'like' ? '#17B26A' : feedback === 'dislike' ? '#A4A7AE' : '#667085'
                  }
                  fill={feedback === 'like' ? '#17B26A' : 'none'}
                  strokeWidth={2}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Like</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="h-auto !p-1 hover:bg-gray-200"
                onClick={() => handleFeedback('dislike')}
                aria-pressed={feedback === 'dislike'}
                aria-label="Dislike answer"
                data-testid="btn-dislike"
                data-track="message_dislike"
                disabled={isSendingFeedback}
              >
                <Icon
                  icon={ThumbsDown}
                  size={12}
                  color={
                    feedback === 'dislike' ? '#A4A7AE' : feedback === 'like' ? '#A4A7AE' : '#667085'
                  }
                  fill={feedback === 'dislike' ? '#A4A7AE' : 'none'}
                  strokeWidth={2}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Dislike</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="h-auto !p-1 hover:bg-gray-200"
                onClick={handleCopyMessage}
                aria-label="Copy full answer"
                data-testid="btn-copy-answer"
                data-track="copy_message"
              >
                <Icon icon={isCopied ? Check : Copy} size={12} strokeWidth={2} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isCopied ? 'Copied!' : 'Copy'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  };

  /**
   * Render status badges (clarification, etc.)
   */
  const renderStatusBadges = () => {
    if (!isClarification || !isLastMessage) return null;

    return (
      <div className={`flex items-center w-full ${isRtl ? 'flex-row-reverse' : 'flex-row'}`}>
        <Badge
          variant="info"
          dir={isRtl ? 'rtl' : 'ltr'}
          leftIcon={<Icon icon={Info} size={18} strokeWidth={2} color="#0B4A6F" />}
        >
          {isRtl ? (
            <div className="flex gap-2">
              سيواصل <span style={{ direction: 'ltr', display: 'inline' }}>Ideoz</span> العمل بعد
              ردك
            </div>
          ) : (
            'Ideoz will continue working after your reply'
          )}
        </Badge>
      </div>
    );
  };

  /**
   * Render MCQ clarification options (SSE clarification.payload.options)
   */
  const renderClarificationOptions = () => {
    if (!isClarification || !isLastMessage || clarificationOptions.length === 0) return null;
    if (isStreaming || isLoading) return null;

    return (
      <div
        className={`flex flex-wrap gap-2 w-full mt-1 ${isRtl ? 'flex-row-reverse' : 'flex-row'}`}
        data-testid="clarification-options"
      >
        {clarificationOptions.map((option) => (
          <Button
            key={option}
            type="button"
            size="xs"
            variant="secondary"
            onClick={() => onSubmit(option)}
            data-testid="clarification-option"
          >
            {option}
          </Button>
        ))}
      </div>
    );
  };

  /**
   * "Thinking…" panel for the assistant turn. Rendered for every assistant
   * message that has a persisted thoughts trail — so historical turns keep
   * their own "Thought for Xs" chip (matches Claude app / Claude Code).
   * The last-message streaming case reuses the live facade props so the
   * panel updates in real time before we've saved a final duration.
   */
  const renderStreamProgress = () => {
    if (message.role !== 'assistant') return null;

    const persistedThoughts = message.thoughts;
    const isLiveTurn = isLastMessage && !!isStreaming;

    // Live turn: render the panel from the facade's in-flight state. If no
    // SSE events have arrived yet, ThinkingPanel shows its own animated
    // "Thinking…" placeholder — so there's never a silent gap between
    // "send" and the first token.
    if (isLiveTurn) {
      return (
        <ThinkingPanel
          thoughts={streamThoughts ?? []}
          isStreaming
          hasStartedResponse={!!hasStartedResponse}
          durationMs={thinkingDurationMs ?? null}
          isRtl={isRtl}
          className="mt-1"
        />
      );
    }

    // Past assistant messages that have a persisted trail keep their own
    // "Thought for Xs" chip.
    if (persistedThoughts && persistedThoughts.length > 0) {
      return (
        <ThinkingPanel
          thoughts={persistedThoughts}
          isStreaming={false}
          hasStartedResponse
          durationMs={message.thinkingDurationMs ?? null}
          isRtl={isRtl}
          className="mt-1"
        />
      );
    }

    return null;
  };

  // ========== Render ==========
  return (
    // Force physical LTR layout for the whole message row so the bubble side is
    // fixed by *who* sent it — user right, assistant left — in every UI language.
    // Text direction inside each bubble is set independently (per-message
    // `isRtl`/`dir`), so Arabic content still renders RTL within its bubble.
    <div dir="ltr" className={`flex flex-col gap-2`} key={message.id}>
      {/* The thinking panel is rendered inside the assistant message, under the
          agent header (see renderMessageContent → IdeozMessage thinkingSlot). */}
      <div
        className={`flex ${
          message.role === 'user' ? 'flex-col items-end' : 'justify-start'
        }`}
      >
        {renderMessageContent()}
      </div>

      {/* Only reveal the artifact pill once the turn has fully finished
          streaming. The artifact body arrives mid-stream while more events are
          still coming (and its persisted id is only hydrated on turn-complete),
          so showing the pill immediately left it sitting next to a live
          "Composing the response…" indicator and gave an un-openable pill. On
          the live last turn we wait for `isStreaming` to clear; past messages
          (loaded from history) always show it. */}
      {message.role === 'assistant' && message.artifact && !(isLastMessage && isStreaming) && (
        <div className="ms-8">
          <ArtifactPill artifact={message.artifact} onOpen={onStreamedArtifactOpen} isRtl={isRtl} />
        </div>
      )}

      {renderFeedbackControls()}
      {message.role === 'assistant' &&
        (prototyperBuildTimeline != null || isWorkbenchBuildLive) && (
          <div className={`w-full  min-w-0 mt-2 `} data-testid="prototyper-build-timeline-wrap">
            <PrototyperReasoningTimeline
              timeline={prototyperBuildTimeline}
              isLive={isWorkbenchBuildLive}
              showTerminalPhases={showPrototyperTimelineTerminalPhases}
              resetKey={message.id}
              // "Built in Xs" on a stopped build told the founder a build existed (finding 2).
              didNotFinish={prototypeBuildDidNotFinish || prototypeSetAside != null}
            />
          </div>
        )}

      {/* Rendered last (after the build timeline) once the turn is a prototyper
          artifact. During a live build it stays visible as a disabled "Building…" card
          instead of disappearing (FE-8). A turn that carries its own build always shows;
          otherwise a preceding user turn is required, because a scope's auto-prompt greeting
          can be mode-tagged 'prototyper' with no build behind it (see the
          `hasPrecedingUserMessage` prop doc). */}
      {message.role === 'assistant' &&
        messageHasPrototyperArtifact(message) &&
        (hasPrecedingUserMessage || messageHasOwnBuild(message)) && (
          <PrototypePill
            onOpen={
              onPrototyperClick
                ? () =>
                    onPrototyperClick((message as any).prototyper_demo_link_id, {
                      llmInteractionId: message.llmInteractionId,
                      didNotFinish: prototypeBuildDidNotFinish,
                    })
                : undefined
            }
            isRtl={isRtl}
            // The prototype's own name, not the build session's label: the label is written
            // from the last user message, so it used to title a prototype "yes".
            title={resolvePrototypeTitle({
              prototypeTitle: message.prototypeTitle,
              prototypeDisplayName: message.prototypeDisplayName,
              prototypeVersionIndex:
                (message.llmInteractionId != null
                  ? prototypeVersionNumber?.(message.llmInteractionId)
                  : undefined) ?? message.prototypeVersionIndex,
              prototypeKind: message.prototypeKind,
              // A number only once the turn has a finished version.
              unfinished:
                isWorkbenchBuildLive ||
                prototypeBuildDidNotFinish ||
                (message.llmInteractionId != null &&
                  isPrototypeVersionSettled?.(message.llmInteractionId) === false),
            })}
            isActive={Boolean(
              message.llmInteractionId &&
              activeSnapshotInteractionId &&
              message.llmInteractionId === activeSnapshotInteractionId
            )}
            isLoading={
              isWorkbenchBuildLive ||
              Boolean(
                message.llmInteractionId &&
                restoreInteractionLoadingId &&
                message.llmInteractionId === restoreInteractionLoadingId
              )
            }
            loadingLabel={
              isWorkbenchBuildLive ? (liveBuildLabel ?? 'Building…') : restoreLoadingLabel
            }
            didNotFinish={prototypeBuildDidNotFinish}
            setAside={prototypeSetAside}
            unfinishedAction={unfinishedButton === 'none' ? undefined : unfinishedButton}
            onContinue={
              onPrototyperClick && unfinishedButton !== 'none'
                ? () =>
                    onPrototyperClick((message as any).prototyper_demo_link_id, {
                      llmInteractionId: message.llmInteractionId,
                      continueBuild: true,
                      didNotFinish: true,
                    })
                : undefined
            }
          />
        )}
      {message.role === 'assistant' && prototypeFixSlot ? (
        <div className="w-full mt-2" data-testid="message-prototype-fix">
          {prototypeFixSlot}
        </div>
      ) : null}
      {renderAssistantBottomBanner()}
      {/* {renderStatusBadges()} */}
      {renderClarificationOptions()}
      {message.role === 'assistant' && streamingWorkbenchSlot ? (
        <div
          className={`w-full max-w-full min-w-0 mt-2 break-words overflow-x-hidden ${isRtl ? 'mr-7' : 'ml-7'}`}
          data-testid="message-embedded-workbench-activity"
        >
          {streamingWorkbenchSlot}
        </div>
      ) : null}
    </div>
  );
};

export default MessageItem;
